/* eslint-disable react/no-array-index-key */
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { BiLogOut } from "react-icons/bi";

import { IMenuConf, getManagerMenu, getSuperadminMenu } from "conf/menu";
import useAuth from "hooks/useAuth";
import useRouter from "common/hooks/use-router";
import Avatar from "../Avatar/Avatar";

interface IMenu {
  currentId: string;
  setSidebarOpen?: any;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

const menuStyle = "text-white/80 hover:bg-white/20";

function Menu({
  currentId,
  setSidebarOpen,
  collapsed = false,
  onToggleCollapse,
}: IMenu) {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { user: currentUser, signout } = useAuth();

  const renderMenu = (currentMenu: IMenuConf) => {
    return Object.keys(currentMenu).map((key: string, index: number) => {
      const { label, children, ignoreGroup } = currentMenu[key];

      const childrenList = children
        ?.filter((child) => {
          // Vérifier la feature organisation requise
          if (child.requiredOrgFeature) {
            return false;
          }
          // Si pas de droits requis, afficher l'entrée
          if (!child.requiredRights || child.requiredRights.length === 0) {
            return true;
          }
          return true;
        })
        .map(({ id, Icon, label: childLabel }) => {
          const isCurrent = currentId?.includes(id);
          return (
            <Link
              key={id}
              to={`/${id}`}
              className={classNames(
                isCurrent ? "bg-white/25 text-white shadow-sm" : menuStyle,
                "group flex items-center px-2 py-1.5 text-sm font-semibold rounded-md transition-colors",
                collapsed && "justify-center",
              )}
              onClick={setSidebarOpen ? () => setSidebarOpen(false) : () => {}}
            >
              <div
                className={classNames(
                  isCurrent
                    ? "bg-white/20 text-white"
                    : "text-white/70 group-hover:text-white",
                  collapsed ? "" : "mr-3",
                  collapsed && "mx-auto",
                  "flex-shrink-0 h-8 w-8 flex items-center justify-center rounded-md",
                )}
              >
                <Icon
                  className={classNames("flex-shrink-0 h-5 w-5")}
                  aria-hidden="true"
                />
              </div>
              {!collapsed && t(`titlePages.${childLabel}`)}
            </Link>
          );
        });
      return ignoreGroup || !childrenList?.length ? (
        <div
          className="mt-4 mb-2 text-xs text-white/60 font-semibold"
          key={`menu-${index}`}
        >
          {childrenList}
        </div>
      ) : (
        <div key={`menu-group${index}`}>
          {collapsed ? (
            <div
              className="flex justify-center mt-4 mb-2"
              key={`menu-${index}`}
            >
              <div className="w-1 h-1 bg-white/40 rounded-full" />
            </div>
          ) : (
            <div
              className="mt-4 mb-2 text-xs text-white/60 font-semibold"
              key={`menu-${index}`}
            >
              {t(`titleDividers.${label}`)}
            </div>
          )}
          {childrenList}
        </div>
      );
    });
  };

  return (
    <div className="flex flex-col flex-1 h-full bg-gradient-to-b from-secondary to-ternaryMedium">
      <div className="flex items-center justify-between px-4 pt-4 pb-3">
        {!collapsed && (
          <img
            src="/images/logo-white.png"
            className="object-contain h-10"
            alt="Logo"
          />
        )}
        {/* {onToggleCollapse && (
          <button
            type="button"
            onClick={onToggleCollapse}
            className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 rounded transition-colors"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? (
              <ChevronRightIcon className="w-4 h-4" />
            ) : (
              <ChevronLeftIcon className="w-4 h-4" />
            )}
          </button>
        )} */}
      </div>

      {!collapsed && (
        <div className="px-4 py-4 border-b border-white/20">
          <button
            type="button"
            className="w-full p-3 rounded-lg border border-white/20 bg-white/10 hover:bg-white/20 transition-colors text-left"
            onClick={() => navigate("/my-account")}
          >
            <div className="flex items-center gap-3">
              <Avatar
                name={currentUser?.name}
                image={currentUser?.avatar}
                size="8"
              />
              <div className="flex flex-col flex-1 text-white min-w-0">
                <span className="text-sm font-semibold truncate">
                  {currentUser?.name}
                </span>
              </div>
            </div>
          </button>
        </div>
      )}

      <div
        className="overflow-y-auto flex-1 px-4 pb-6
        scrollbar-thin scrollbar-thumb-white/30 scrollbar-track-transparent scrollbar-thumb-rounded scrollbar-track-rounded"
      >
        {renderMenu(
          currentUser?.isSuperadmin || currentUser?.isAdmin
            ? getSuperadminMenu()
            : getManagerMenu(),
        )}

        <button
          type="button"
          className={classNames(
            "opacity-80 hover:opacity-100 group flex items-center px-2 py-1.5 text-sm font-semibold rounded-md text-white/80 hover:bg-white/20 w-full transition-colors",
            collapsed && "justify-center px-2",
          )}
          onClick={() => {
            signout();
            navigate("/login");
          }}
        >
          <div
            className={classNames(
              collapsed ? "" : "mr-3",
              collapsed && "mx-auto",
              "flex-shrink-0 h-8 w-8 flex items-center justify-center rounded-md",
            )}
          >
            <BiLogOut
              className={classNames(
                "text-white/70 group-hover:text-white",
                "flex-shrink-0 h-5 w-5",
              )}
              aria-hidden="true"
            />
          </div>
          {!collapsed && t("titlePages.logout")}
        </button>
      </div>
    </div>
  );
}

export default Menu;
